'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { AppAvatar } from '@core/ui/components/AppAvatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@core/ui/primitives/tooltip';
import type { CollabPresencePayload } from '@/core/helpers/react/collaboration/collab-presence-events';
import { COLLAB_PRESENCE_EVENT } from '@/core/helpers/react/collaboration/collab-presence-events';

function getStatusDotClass(status: CollabPresencePayload['status']): string {
  switch (status) {
    case 'connected':
      return 'bg-success';
    case 'connecting':
      return 'bg-warning';
    case 'disconnected':
    default:
      return 'bg-destructive';
  }
}

function isEmail(value: string): boolean {
  return value.includes('@');
}

export function CollaborationPresenceHandle({ className }: { className?: string }) {
  const [presence, setPresence] = useState<CollabPresencePayload | null>(null);
  const lastSignatureRef = useRef<string>('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as CollabPresencePayload | undefined;
      if (!detail) {
        setPresence(null);
        return;
      }

      const signature = `${detail.room ?? ''}|${detail.status}|${detail.users
        .map((u) => `${u.clientId}|${u.id ?? ''}|${u.email ?? ''}|${u.avatar ?? ''}|${u.name}|${u.color}|${u.isSelf ? 1 : 0}`)
        .join(';')}`;

      if (signature === lastSignatureRef.current) return;
      lastSignatureRef.current = signature;

      if (detail.users.length === 0) {
        setPresence(null);
        return;
      }

      setPresence(detail);
    };

    window.addEventListener(COLLAB_PRESENCE_EVENT, handler as EventListener);
    return () => window.removeEventListener(COLLAB_PRESENCE_EVENT, handler as EventListener);
  }, []);

  const users = presence?.users ?? [];
  const status = presence?.status ?? 'disconnected';

  const shown = users.slice(0, 4);
  const overflow = Math.max(0, users.length - shown.length);

  const label = useMemo(() => {
    if (!presence) return '';
    const names = users.map((u) => (u.isSelf ? `${u.name} (you)` : u.name));
    return names.join(', ');
  }, [presence, users]);

  if (!presence) return null;

  return (
    <div
      className={cn(
        // `-mt-px` overlaps the navbar bottom border so the handle feels like a seamless extension.
        'pointer-events-auto absolute right-0 top-full -mt-px z-50',
        className
      )}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex items-center gap-2 px-2 py-1',
              'rounded-b-xl rounded-t-none',
              'border border-t-0 border-white/5',
              'bg-main-background/30 backdrop-blur-xl',
              'shadow-lg shadow-black/10 dark:shadow-black/30'
            )}
            style={{ WebkitBackdropFilter: 'blur(24px)' }}>
            <span className={cn('h-2 w-2 rounded-full', getStatusDotClass(status))} aria-hidden='true' />
            <div className='flex items-center'>
              <div className='flex -space-x-2'>
                {shown.map((user) => {
                  const identity = user.id ?? null;
                  const avatarProps: { userId?: string; email?: string } = {};
                  if (user.email) avatarProps.email = user.email;
                  else if (identity) {
                    if (isEmail(identity)) avatarProps.email = identity;
                    else avatarProps.userId = identity;
                  }

                  return (
                    <AppAvatar
                      key={user.clientId}
                      {...avatarProps}
                      name={user.name}
                      size='6'
                      className='border-2 border-background'
                      style={{ borderColor: user.color }}
                    />
                  );
                })}
              </div>
              {overflow > 0 && <span className='ml-2 text-xs text-muted-foreground'>+{overflow}</span>}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side='bottom' sideOffset={8}>
          <div className='max-w-[22rem] text-balance'>{label}</div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export default CollaborationPresenceHandle;
